/**
 * PanelHost — chrome wrapper for any panel (matches Figma 559:10449).
 *
 *   - shared structural styles (positioning, flex, rounded card, shadow)
 *     live on `.panel` in panels.css — this component only assigns
 *     theme-dependent surface colors and feature toggles
 *   - applies theme-occurra + light/dark wrapper so per-panel theme works
 *   - default header: the Occurra <PanelHeader> row, 44px, with the title and
 *     the corner row (components/occurra/panel/PanelHeader.tsx)
 *   - one top-right action row per panel, holding the × and whatever chrome
 *     buttons the panel itself contributes through <PanelActions> (see
 *     app/PanelActions.tsx). The row is the host's, so every button in it is
 *     anchored to the panel's own right edge, one flex line with the ×.
 *   - opt out of the header via config.isHeaderless = true; the panel renders
 *     its own header (e.g. AI chat shows just an × overlay; camera view
 *     overlays its title on top of the video), and the corner row floats over
 *     it at the same 6px inset. Its measured width is published on the panel
 *     as --occ-panel-actions-width, which a `shouldReserve` header keeps clear of.
 *   - opt out of the card surface entirely via config.isTransparent = true; the
 *     panel content sits directly on the page background with no card fill,
 *     rounding, or shadow (e.g. AI chat). The .panel[data-transparent] CSS
 *     rule strips the rounded/shadow defaults; this component just skips
 *     the bg color.
 *   - opt in to overflow clipping via config.shouldClip = true (e.g. camera view
 *     so the video clips to the rounded corners). Off by default so popovers
 *     and other floating children inside light panels can escape the card.
 *
 * Light panels use bg-occ-background-panel. Dark panels (camera view) use
 * bg-occ-background-panel inside their own dark scope (which resolves to
 * pure black per the Occurra dark palette).
 */

import { AlertTriangle, Pin, RotateCcw } from 'lucide-react';
import type { CSSProperties, ErrorInfo, ReactElement, ReactNode, RefObject } from 'react';
import React, {
    Suspense, useCallback, useLayoutEffect, useMemo, useRef, useState,
} from 'react';

import { CancelIcon } from '@/components/occurra/icons/CancelIcon';
import {
    PanelHeader, PanelHeaderButton, PanelHeaderTitle, PanelLoader,
} from '@/components/occurra/panel';
import { useLatest } from '@/hooks/useLatest';
import { sentryReporter } from '@/lib/sentry';
import { cn } from '@/lib/utils';
import type {
    CameraPickAction, CameraSelection, OpenPanelOptions, PanelConfig, PanelContextValue,
    PanelHostBindings, PanelId, PanelMode,
} from '@/types/panels';
import { PanelTheme, PanelTransitionState, Theme } from '@/types/panels';

import { PanelContext } from './PanelContext';
import { useStageResizing } from './stageMotion';

interface PanelErrorBoundaryProps {
    /** The panel id; a new one remounts the boundary and clears its error. */
    resetKey: string;
    children: ReactNode;
}

interface PanelErrorBoundaryState {
    error: Error | null;
}

/** What the transition gates read. */
interface TransitionGate {
    transitionState: PanelTransitionState;
    willLayoutAnimate: boolean;
    isPanelTransitionOver: boolean;
}

/** The content a frozen panel keeps showing: its children and their mode. */
interface PanelContent {
    children: ReactNode;
    mode: PanelMode;
}

/**
 * Per-panel error boundary.
 *
 * Catches render-phase and lifecycle errors thrown by any descendant so a
 * single broken panel (e.g. a cloud-mode method that throws "not
 * supported") fails inside its own slot instead of taking down the
 * whole shell. Caveat: doesn't catch promise rejections from event
 * handlers or async effects — those need their own try/catch (most of
 * our fetches already have one).
 *
 * The `resetKey` prop is the panel id; React unmounts/remounts the
 * boundary when it changes, which automatically clears stale error
 * state if the user closes and reopens the same panel.
 */
class PanelErrorBoundary
    extends React.Component<PanelErrorBoundaryProps, PanelErrorBoundaryState> {
    constructor(props: PanelErrorBoundaryProps) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error: Error): PanelErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface in DevTools — React's own overlay is suppressed by the
    // boundary, but the console + componentStack are still useful.
        console.error('[panel]', this.props.resetKey, error, info.componentStack);
        sentryReporter.reportReactCrash(error, info);
    }
    retry = (): void => this.setState({ error: null });

    render(): ReactNode {
        const { error } = this.state;
        if (!error) {
            return this.props.children;
        }
        return (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center text-sm">
                <AlertTriangle
                    className="size-6 text-amber-500"
                    aria-hidden="true"
                />
                <div className="flex flex-col gap-1">
                    <span className="font-medium">Something went wrong in this panel</span>
                    <span className="font-mono text-xs text-occ-foreground-tertiary">
                        {error.message || String(error)}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={this.retry}
                    className={`inline-flex items-center gap-1.5 rounded border border-occ-border-1
                        px-2.5 py-1 text-xs not-disabled:hover:bg-occ-foreground-25
                        active:bg-occ-foreground-50`}
                >
                    <RotateCcw className="size-3.5" />
                    Retry
                </button>
            </div>
        );
    }
}

/** Whether the React subtree may take a fresh render: only on a settled stage. */
export function canRefreshPanelContent({
    transitionState, willLayoutAnimate, isPanelTransitionOver,
}: TransitionGate): boolean {
    return transitionState === PanelTransitionState.Stable &&
        !willLayoutAnimate && isPanelTransitionOver;
}

export function selectPanelContent<T>(
    shouldRefreshContent: boolean, currentContent: T, committedContent: T | null,
): T | null {
    return shouldRefreshContent ? currentContent : committedContent;
}

/**
 * Is this panel's slot easing on its own right now: its entrance or exit, or a
 * layout transition in flight (a sibling opening, the chat drawer sliding)?
 * False during a manual drag, whose slots follow the pointer rather than ease,
 * which is why `isPanelTransitionOver` excludes drags and `isStageResizing`
 * vetoes explicitly.
 */
export function isPanelSlotEasing({
    transitionState, willLayoutAnimate, isPanelTransitionOver, isStageResizing = false,
}: TransitionGate & { isStageResizing?: boolean }): boolean {
    if (isStageResizing) {
        return false;
    }
    return transitionState !== PanelTransitionState.Stable ||
        willLayoutAnimate || !isPanelTransitionOver;
}

/**
 * Publish the floating corner row's width on the panel, for a headerless
 * panel's own header to keep clear of (`PanelHeader reserve`).
 */
export function syncActionsWidth(panel: HTMLElement, row: HTMLElement): void {
    panel.style.setProperty('--occ-panel-actions-width', `${row.offsetWidth}px`);
}

/**
 * Give a body its own compositor layer while its slot eases. Its content lays
 * out for the slot's width on every frame; the layer is what lets a panel that
 * only slides be moved from one recorded display list instead of repainting
 * every image in it on every frame.
 */
export function syncPanelLayer(node: HTMLElement | null, isElevated: boolean): void {
    if (!node) {
        return;
    }
    if (isElevated) {
        node.style.setProperty('will-change', 'transform');
        node.style.setProperty('transform', 'translateZ(0)');
    } else {
        node.style.removeProperty('will-change');
        node.style.removeProperty('transform');
    }
}

interface PanelHostProps extends TransitionGate {
    id: PanelId;
    config: PanelConfig;
    bindings: PanelHostBindings;
    /** Absent for a surface outside the stage row, which has nothing to widen over. */
    expandPanel?: (id: PanelId) => void;
    canExpand?: boolean;
    /** Absent for a surface outside the stage row, which has no free room to widen into. */
    widenPanel?: (id: PanelId, byPx: number) => boolean;
    hasPanelToRight?: boolean;
    /** Floats over the row at the stage's right edge: the row has no room for it. */
    isOverlay?: boolean;
    /** Present while the panel floats: narrows the row until the panel docks in it. */
    dockPanel?: (id: PanelId) => void;
    canDock?: boolean;
    style?: CSSProperties;
    children: ReactNode;
}

export const PanelHost = ({
    id,
    config,
    bindings,
    expandPanel,
    canExpand = false,
    widenPanel,
    transitionState,
    willLayoutAnimate,
    isPanelTransitionOver,
    hasPanelToRight = false,
    isOverlay = false,
    dockPanel,
    canDock = false,
    style,
    children,
}: PanelHostProps): ReactElement => {
    const { mode, theme, onClose, openPanel, setPanelProp, selectCamera } = bindings;
    const themeClass = config.themes === PanelTheme.Any ? theme : config.themes;
    const isDark = themeClass === Theme.Dark;
    const panelRef = useRef<HTMLElement | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const floatingRowRef = useRef<HTMLDivElement>(null);

    // Hold both the React element and the context values seen by its subtree
    // while the slot is moving. Reusing the same element object lets React bail
    // out of the child tree entirely; newly entering panels have no settled
    // snapshot yet, so their content does not mount until the entrance settles.
    const shouldRefreshContent = canRefreshPanelContent({
        transitionState,
        willLayoutAnimate,
        isPanelTransitionOver,
    });
    // A live chat/nav/splitter drag reallocates through React on every
    // ResizeObserver sample. Freezing subtree IDENTITY keeps those per-frame
    // renders to the cheap slot shells — a mounted camera view re-rendering per
    // pointer sample is what made shell drags drop frames. Widths stay fluid
    // throughout, so CSS (container queries, size-full video) and the content's
    // own observers keep tracking the slot on every frame.
    const isStageResizing = useStageResizing();
    const isElementFrozen = !shouldRefreshContent || isStageResizing;
    // The content of an interrupted render goes with that render, so a later frozen commit never
    // shows it.
    const [committedContent, setCommittedContent] = useState<PanelContent | null>(null);
    if (!isElementFrozen && (committedContent === null ||
        committedContent.children !== children || committedContent.mode !== mode)) {
        setCommittedContent({ children, mode });
    }
    const currentContent: PanelContent = { children, mode };
    const displayedContent = selectPanelContent(
        !isElementFrozen,
        currentContent,
        committedContent,
    );
    const contentMode = displayedContent?.mode ?? mode;

    const isSlotEasing = isPanelSlotEasing({
        transitionState,
        willLayoutAnimate,
        isPanelTransitionOver,
        isStageResizing,
    });

    useLayoutEffect((): void => {
        syncPanelLayer(bodyRef.current, isSlotEasing);
    }, [isSlotEasing]);

    // The bindings' callbacks come from AppShell and get a new identity on
    // every state tick. We bind them through refs so the context stays stable
    // across sibling changes.
    const onCloseRef = useLatest(onClose);
    const openPanelRef = useLatest(openPanel);
    const setPanelPropRef = useLatest(setPanelProp);
    const selectCameraRef = useLatest(selectCamera);
    const expandPanelRef = useLatest(expandPanel);
    const widenPanelRef = useLatest(widenPanel);

    const close = useCallback((): void => onCloseRef.current(id), [id, onCloseRef]);
    const openPanelStable = useCallback(
        (
            nextId: PanelId, nextProps?: Record<string, unknown>, options?: OpenPanelOptions,
        ): void => {
            openPanelRef.current(nextId, nextProps, options);
        }, [openPanelRef]);
    const setProp = useCallback((key: string, value: unknown): void => {
        setPanelPropRef.current(id, key, value);
    }, [id, setPanelPropRef]);
    // Close / set-prop on *another* panel by id (vs `close` / `setProp`, which
    // act on this panel). Lets a panel drive a drawer it owns — e.g.
    // the rule editor toggling and re-targeting rule_estimate.
    const closePanelById = useCallback(
        (otherId: PanelId): void => onCloseRef.current(otherId), [onCloseRef]);
    const setPanelPropById = useCallback(
        (otherId: PanelId, key: string, value: unknown): void => {
            setPanelPropRef.current(otherId, key, value);
        }, [setPanelPropRef],
    );
    // Picking a camera means different things per shell — v2 routes to the
    // current app mode's surface, v3 retargets whichever camera panels are
    // already open. The shell owns the rule; panels just say "this one".
    const selectCameraStable = useCallback(
        (cameraId: string, opts: CameraSelection): CameraPickAction | undefined => (
            selectCameraRef.current(cameraId, opts)
        ), [selectCameraRef]);
    // Widen this panel over its right-hand neighbours (app/panelExpand.ts).
    const expand = useCallback((): void => expandPanelRef.current?.(id), [id, expandPanelRef]);
    const widen = useCallback(
        (byPx: number): boolean => widenPanelRef.current?.(id, byPx) ?? false,
        [id, widenPanelRef],
    );

    // The action row's DOM node, for <PanelActions> to portal into. State, not a
    // ref, so the portal renders as soon as the node exists — one extra render
    // per panel MOUNT, after which it is as stable as the rest of the ctx.
    const [actionsNode, setActionsNode] = useState<HTMLElement | null>(null);

    // The floating row's width changes as a panel's own buttons come and go, so
    // it is followed, not read once. Before paint, so a header never shows
    // under the row for a frame.
    useLayoutEffect((): (() => void) | undefined => {
        const panel = panelRef.current;
        const row = floatingRowRef.current;
        if (!panel || !row) {
            return undefined;
        }
        syncActionsWidth(panel, row);
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver((): void => syncActionsWidth(panel, row));
        observer.observe(row);
        return (): void => observer.disconnect();
    }, [config.isHeaderless]);

    // A panel can withdraw the × while its content offers a better way out —
    // camera_view does it for an expanded camera, where the only sensible exit
    // is back to the layout that camera came from.
    const [isCloseHidden, setCloseHidden] = useState(false);
    const [isOutlined, setOutlined] = useState(false);

    const ctx = useMemo((): PanelContextValue => ({
        panelId: id,
        mode: contentMode,
        close,
        openPanel: openPanelStable,
        setProp,
        closePanel: closePanelById,
        setPanelProp: setPanelPropById,
        selectCamera: selectCameraStable,
        expand,
        canExpand,
        widen,
        actionsNode,
        setCloseHidden,
        setOutlined,
    }), [id, contentMode, close, openPanelStable, setProp, closePanelById,
        setPanelPropById, selectCameraStable, expand, canExpand, widen, actionsNode]);

    // When `transparent: true` is set in the panel config the host skips the
    // card chrome: no surface fill, no corner radius, no shadow. The panel
    // content sits directly on the page background. Used by AI chat per
    // Figma 559:10873.
    const isTransparent = !!config.isTransparent;

    // A video panel is the picture itself: panels.css paints it black and drops
    // the chrome, so the host adds no fill of its own.
    const fillClass = config.hasSelectedSurface
        ? 'bg-occ-background-card'
        : 'bg-occ-background-panel';
    const textClass = isDark ? 'text-occ-foreground-high-contrast' : 'text-occ-foreground-main';
    const surfaceClass = isTransparent || config.hasVideo ? textClass : cn(fillClass, textClass);

    return (
        <section
            ref={panelRef}
            data-panel-id={id}
            data-transparent={isTransparent || undefined}
            data-video={config.hasVideo || undefined}
            data-has-right-panel={hasPanelToRight || undefined}
            data-selected-surface={config.hasSelectedSurface || undefined}
            data-overlay={isOverlay || undefined}
            data-state={transitionState !== PanelTransitionState.Stable ? transitionState : undefined}
            style={style}
            className={cn(
                'panel theme-occurra',
                isDark && 'dark',
                config.shouldClip && 'overflow-hidden',
                surfaceClass,
                // On top of the content, which runs to the edges of the panel.
                isOutlined && `after:pointer-events-none after:absolute after:inset-0
                    after:rounded-[inherit] after:border after:border-occ-brand-200`,
            )}
        >
            <PanelContext.Provider value={ctx}>
                {!config.isHeaderless && (
                    <PanelHeader end={(
                        <ActionRow
                            slotRef={setActionsNode}
                            onClose={(): void => onClose(id)}
                            onPin={dockPanel && ((): void => dockPanel(id))}
                            canPin={canDock}
                            title={config.title}
                            shouldHideClose={isCloseHidden}
                        />
                    )}
                    >
                        <PanelHeaderTitle>{config.title}</PanelHeaderTitle>
                    </PanelHeader>
                )}
                {config.isHeaderless && (
                    <ActionRow
                        rowRef={floatingRowRef}
                        isFloating
                        slotRef={setActionsNode}
                        onClose={(): void => onClose(id)}
                        onPin={dockPanel && ((): void => dockPanel(id))}
                        canPin={canDock}
                        title={config.title}
                        shouldHideClose={isCloseHidden}
                    />
                )}
                <div
                    ref={bodyRef}
                    className="panel-body"
                >
                    <PanelErrorBoundary resetKey={id}>
                        {/* The panel's module downloads when the panel first renders. */}
                        <Suspense fallback={(
                            <div
                                role="status"
                                aria-label="Loading panel"
                                data-panel-loading=""
                                className="relative h-full"
                            >
                                <PanelLoader />
                            </div>
                        )}
                        >
                            {displayedContent?.children ?? null}
                        </Suspense>
                    </PanelErrorBoundary>
                </div>
            </PanelContext.Provider>
        </section>
    );
};

/**
 * The panel's top-right controls: the panel's own buttons (portalled into the
 * slot by <PanelActions>), the pin of a floating overlay, then the ×, laid out
 * as one flex row. In a headered panel the row rides the header; in a
 * headerless one it floats over the content at the panel's corner. Either way
 * it is a child of `.panel`, so it rides the panel's own edge.
 *
 * The slot is `display: contents`, so a panel contributing a row of its own
 * keeps its spacing while still sitting in this flex line.
 */
interface ActionRowProps {
    slotRef: (node: HTMLDivElement | null) => void;
    rowRef?: RefObject<HTMLDivElement | null>;
    onClose: () => void;
    /** Present while the panel floats over the row. */
    onPin?: () => void;
    canPin?: boolean;
    title: string;
    isFloating?: boolean;
    shouldHideClose?: boolean;
}

const ActionRow = ({
    slotRef, rowRef, onClose, onPin, canPin = false, title, isFloating = false,
    shouldHideClose = false,
}: ActionRowProps): ReactElement => (
    <div
        ref={rowRef}
        data-floating={isFloating || undefined}
        className="occ-panel-actions"
    >
        <div
            ref={slotRef}
            className="occ-panel-actions-slot"
        />
        {onPin && (
            <PanelHeaderButton
                tooltip="Pin panel"
                disabled={!canPin}
                disabledReason="Not enough room beside the other panels"
                aria-label={`Pin ${title}`}
                onClick={(event): void => {
                    event.stopPropagation();
                    onPin();
                }}
            >
                <Pin />
            </PanelHeaderButton>
        )}
        {!shouldHideClose && (
            <PanelHeaderButton
                tooltip="Close panel"
                aria-label={`Close ${title}`}
                onClick={(event): void => {
                    event.stopPropagation();
                    onClose();
                }}
            >
                <CancelIcon />
            </PanelHeaderButton>
        )}
    </div>
);
